import { useConnectStore } from '@/libs/api/connect/connect.store.ts'
import { computed, type ComputedRef, ref } from 'vue'
import type { LSelectType } from '@/components/organisms/forms/entry/EntryForm.vue'
import { generateCurrentDate } from '@/utils/date-util.ts'
import type { SettingsLinexo } from '@/libs/api/connect/connect.types.ts'
import { getSelectValue } from '@/utils/type.util.ts'
import { translate } from '@/i18n/i18n'

export type EntryFormDto = {
  deviceClassId: string
  purchaseDate: string | undefined
  purchasePrice: string | undefined
}

export const useEntryFormState = () => {
  const connectStore = useConnectStore()

  const inputPurchaseDate = ref<string>()
  const inputPurchasePrice = ref<string>()
  const selectDeviceClass = ref<LSelectType>()

  const hasError = ref(false)

  const deviceClassOptions = computed(() => {
    const deviceClasses = connectStore?.getSelectedDeviceClasses ?? []

    return deviceClasses.map((it) => {
      return {
        label: translate(it.name),
        value: it.id,
        icon: it.icon
      }
    })
  })

  const formDataDto: ComputedRef<EntryFormDto> = computed(() => {
    const deviceClass = selectDeviceClass.value
    const deviceClassId = getSelectValue(deviceClass)

    return {
      deviceClassId,
      purchaseDate: inputPurchaseDate.value,
      purchasePrice: inputPurchasePrice.value
    }
  })

  const isFormFilled = computed(() => {
    return !!inputPurchasePrice.value && !!inputPurchaseDate.value && !!selectDeviceClass.value
  })

  function _initPurchaseDateDefaults(settings: SettingsLinexo) {
    const { useCurrentDateDefault, overwriteDefaultDate } =
      settings.defaultInputValues?.purchaseDate ?? {}

    if (useCurrentDateDefault) {
      inputPurchaseDate.value = generateCurrentDate()
    } else if (!!overwriteDefaultDate) {
      inputPurchaseDate.value = overwriteDefaultDate
    }
  }

  function initLinexoDefaultValues() {
    const settings = connectStore.getLinexoSettings
    // Default settings are just applied in case of mini variant
    if (settings?.defaultInputValues && settings?.siteConnectVariant === 'mini') {
      selectDeviceClass.value = settings.defaultInputValues?.deviceClassId ?? ''
      inputPurchasePrice.value = settings.defaultInputValues?.purchasePrice?.toString()

      _initPurchaseDateDefaults(settings)
    }
  }

  return {
    inputPurchaseDate,
    inputPurchasePrice,
    selectDeviceClass,
    deviceClassOptions,
    hasError,
    isFormFilled,
    formDataDto,
    initLinexoDefaultValues
  }
}
